import type { KeySignature, Score } from './types';

/** Editor note values are stored in quarter-note units. A dotted quarter is
 * 1.5 quarters but still starts on a quarter/eighth subdivision. */
export function entrySnapGrid(duration: number): number {
  return Number.isInteger(duration) ? Math.min(1, duration) : Math.min(0.5, duration);
}

/** Convert a written value in quarter-note units to the denominator-unit beat
 * domain used by score events (an eighth is 1 beat in 6/8, 0.5 in 4/4). */
export function entryDurationInBeats(
  score: Score,
  measureId: string,
  durationQuarters: number,
): number {
  let denominator = 4;
  for (const measure of score.form) {
    if (measure.timeSig) denominator = measure.timeSig[1];
    if (measure.id === measureId) break;
  }
  return durationQuarters * (denominator / 4);
}

/** Onset grid in the active measure's denominator-unit beat domain. */
export function entryGridInBeats(
  score: Score,
  measureId: string,
  durationQuarters: number,
): number {
  return entryDurationInBeats(score, measureId, entrySnapGrid(durationQuarters));
}

/** Session-only location of Score Studio's note-entry caret. Measure ids keep
 * the caret stable when a bar is inserted elsewhere in the form. */
export interface ScoreEntryCursor {
  measureId: string;
  /** 1-based beat in the meter's denominator unit. */
  beat: number;
  /** Pending concert pitch used by A-G entry and empty-caret pitch arrows. */
  midi: number;
}

export interface EntryMeasure {
  id: string;
  beats: number;
}

export type EntryCursorMove = { cursor: ScoreEntryCursor } | { appendAfterId: string };

const ENTRY_EPSILON = 1e-9;

/** Effective bar lengths, including inherited meters and pickup measures. */
export function entryMeasures(score: Score): EntryMeasure[] {
  let meter: [number, number] = [4, 4];
  return score.form.map((measure) => {
    if (measure.timeSig) meter = measure.timeSig;
    return { id: measure.id, beats: measure.pickupBeats ?? meter[0] };
  });
}

/** Move to the next/previous boundary of the active onset grid. This is
 * deliberately grid-based rather than `beat + grid`: after changing from an
 * eighth to a quarter while sitting at beat 2.5, Right lands on beat 3. */
export function stepEntryCursor(
  cursor: ScoreEntryCursor,
  measures: readonly EntryMeasure[],
  grid: number | ((measure: EntryMeasure) => number),
  direction: -1 | 1,
  byMeasure = false,
): EntryCursorMove {
  if (measures.length === 0) return { cursor };
  const index = measures.findIndex((measure) => measure.id === cursor.measureId);
  if (index < 0) return { cursor };
  const measure = measures[index];
  const gridFor = (target: EntryMeasure) =>
    Math.max(ENTRY_EPSILON, typeof grid === 'function' ? grid(target) : grid);
  const activeGrid = gridFor(measure);
  const cursorOffset = Math.max(0, cursor.beat - 1);
  const capacity = Math.max(ENTRY_EPSILON, measure.beats);

  if (direction > 0) {
    const nextIndex = index + 1;
    const nextOffset = byMeasure
      ? capacity
      : (Math.floor((cursorOffset + ENTRY_EPSILON) / activeGrid) + 1) * activeGrid;
    if (nextOffset >= capacity - ENTRY_EPSILON) {
      if (nextIndex >= measures.length) return { appendAfterId: measure.id };
      return {
        cursor: { ...cursor, measureId: measures[nextIndex].id, beat: 1 },
      };
    }
    return { cursor: { ...cursor, beat: 1 + nextOffset } };
  }

  if (byMeasure) {
    if (index === 0) return { cursor: { ...cursor, beat: 1 } };
    return { cursor: { ...cursor, measureId: measures[index - 1].id, beat: 1 } };
  }

  const previousOffset = (Math.ceil((cursorOffset - ENTRY_EPSILON) / activeGrid) - 1) * activeGrid;
  if (previousOffset >= -ENTRY_EPSILON) {
    return { cursor: { ...cursor, beat: 1 + Math.max(0, previousOffset) } };
  }
  if (index === 0) return { cursor: { ...cursor, beat: 1 } };
  const previous = measures[index - 1];
  const previousCapacity = Math.max(ENTRY_EPSILON, previous.beats);
  const previousGrid = gridFor(previous);
  const lastGridOffset = Math.max(
    0,
    Math.floor((previousCapacity - ENTRY_EPSILON) / previousGrid) * previousGrid,
  );
  return {
    cursor: {
      ...cursor,
      measureId: previous.id,
      beat: 1 + lastGridOffset,
    },
  };
}

/** Advance by an event's actual written duration. Keyboard-entered notes use
 * this so a half note moves straight to its end even when the onset grid is a
 * quarter. Entry is clamped at a barline, so crossing always lands on beat 1. */
export function advanceEntryCursor(
  cursor: ScoreEntryCursor,
  measures: readonly EntryMeasure[],
  duration: number,
): EntryCursorMove {
  if (measures.length === 0) return { cursor };
  const index = measures.findIndex((measure) => measure.id === cursor.measureId);
  if (index < 0) return { cursor };
  const measure = measures[index];
  const endOffset = cursor.beat - 1 + Math.max(ENTRY_EPSILON, duration);
  if (endOffset >= measure.beats - ENTRY_EPSILON) {
    if (index + 1 >= measures.length) return { appendAfterId: measure.id };
    return { cursor: { ...cursor, measureId: measures[index + 1].id, beat: 1 } };
  }
  return { cursor: { ...cursor, beat: 1 + endOffset } };
}

/** Keep a session caret valid after undo or a structural form edit. */
export function reconcileEntryCursor(
  score: Score,
  cursor: ScoreEntryCursor | null,
  grid: number,
): ScoreEntryCursor | null {
  if (!cursor) return null;
  const measures = entryMeasures(score);
  if (measures.length === 0) return null;
  const hit = measures.find((measure) => measure.id === cursor.measureId);
  if (hit) {
    const safeGrid = Math.max(ENTRY_EPSILON, grid);
    const lastGridOffset = Math.max(
      0,
      Math.floor((hit.beats - ENTRY_EPSILON) / safeGrid) * safeGrid,
    );
    const beat =
      cursor.beat >= 1 + hit.beats - ENTRY_EPSILON ? 1 + lastGridOffset : Math.max(1, cursor.beat);
    return {
      ...cursor,
      beat,
      midi: Math.min(127, Math.max(0, Math.round(cursor.midi))),
    };
  }
  const last = measures[measures.length - 1];
  const safeGrid = Math.max(ENTRY_EPSILON, grid);
  const lastGridOffset = Math.max(
    0,
    Math.floor((last.beats - ENTRY_EPSILON) / safeGrid) * safeGrid,
  );
  return {
    ...cursor,
    measureId: last.id,
    beat: 1 + lastGridOffset,
    midi: Math.min(127, Math.max(0, Math.round(cursor.midi))),
  };
}

/** MuseScore's top-row duration convention. */
export const DURATION_SHORTCUTS: Readonly<Record<string, number>> = {
  '3': 0.25,
  '4': 0.5,
  '5': 1,
  '6': 2,
  '7': 4,
};

/** Toggle the augmentation dot on common entry values. */
export function toggleDottedDuration(duration: number): number {
  const dottedBase = [0.25, 0.5, 1, 2, 4].find((base) => Math.abs(duration - base * 1.5) < 1e-9);
  return dottedBase ?? duration * 1.5;
}

const MAJOR_FIFTHS: Record<string, number> = {
  C: 0,
  G: 1,
  D: 2,
  A: 3,
  E: 4,
  B: 5,
  'F#': 6,
  'C#': 7,
  F: -1,
  Bb: -2,
  Eb: -3,
  Ab: -4,
  Db: -5,
  Gb: -6,
  Cb: -7,
};
const MINOR_FIFTHS: Record<string, number> = {
  A: 0,
  E: 1,
  B: 2,
  'F#': 3,
  'C#': 4,
  'G#': 5,
  'D#': 6,
  'A#': 7,
  D: -1,
  G: -2,
  C: -3,
  F: -4,
  Bb: -5,
  Eb: -6,
  Ab: -7,
};
const NATURAL_PC: Record<string, number> = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };
const SHARP_ORDER = ['F', 'C', 'G', 'D', 'A', 'E', 'B'];
const FLAT_ORDER = ['B', 'E', 'A', 'D', 'G', 'C', 'F'];

/** One-shot accidental selected for the next A-G entry. `key` follows the
 * active written key signature; the other choices explicitly override it. */
export type EntryAccidental = 'key' | 'natural' | 'sharp' | 'flat';

/** Pitch class produced by a letter key under the written key signature. */
export function entrySpellingForLetter(
  letter: string,
  key: KeySignature | undefined,
  accidental: EntryAccidental = 'key',
): string {
  const step = letter.toUpperCase();
  const natural = NATURAL_PC[step];
  if (natural === undefined) throw new Error(`Unsupported entry letter: ${letter}`);
  if (accidental !== 'key') {
    return `${step}${accidental === 'sharp' ? '#' : accidental === 'flat' ? 'b' : ''}`;
  }
  const fifths = key
    ? ((key.mode === 'minor' ? MINOR_FIFTHS[key.tonic] : MAJOR_FIFTHS[key.tonic]) ?? 0)
    : 0;
  const suffix =
    fifths > 0 && SHARP_ORDER.slice(0, fifths).includes(step)
      ? '#'
      : fifths < 0 && FLAT_ORDER.slice(0, -fifths).includes(step)
        ? 'b'
        : '';
  return `${step}${suffix}`;
}

/** Pitch class produced by a letter key under the written key signature. */
export function entryLetterPitchClass(
  letter: string,
  key: KeySignature | undefined,
  accidental: EntryAccidental = 'key',
): number {
  const spelling = entrySpellingForLetter(letter, key, accidental);
  const step = spelling[0];
  const natural = NATURAL_PC[step];
  const altered = natural + (spelling.endsWith('#') ? 1 : spelling.endsWith('b') ? -1 : 0);
  return ((altered % 12) + 12) % 12;
}

/** Nearest register for A-G entry, using the key signature at the caret. */
export function entryMidiForLetter(
  letter: string,
  anchorMidi: number,
  key: KeySignature | undefined,
  accidental: EntryAccidental = 'key',
): number {
  const pc = entryLetterPitchClass(letter, key, accidental);
  const anchor = Math.min(127, Math.max(0, Math.round(anchorMidi)));
  const below = anchor - ((((anchor - pc) % 12) + 12) % 12);
  const above = below + 12;
  const candidates = [below, above].filter((midi) => midi >= 0 && midi <= 127);
  return (
    candidates.sort((a, b) => Math.abs(a - anchor) - Math.abs(b - anchor) || b - a)[0] ?? anchor
  );
}

/** Inherited written key signature at a cursor measure. */
export function entryKeySignature(score: Score, measureId: string): KeySignature | undefined {
  let key: KeySignature | undefined;
  for (const measure of score.form) {
    if (measure.keySig) key = measure.keySig;
    if (measure.id === measureId) break;
  }
  return key;
}
